import React, { useEffect, useState } from 'react';
import { Crown, Infinity as Inf, Zap, MessageSquare, Layers } from 'lucide-react';
import { getLimits, getPrime, isDevUnlimited } from '../../lib/quota';

export const QuotaWidget = ({ onUpgrade }) => {
  const [data, setData] = useState(() => ({ ...getLimits(), prime: getPrime() }));

  useEffect(() => {
    const refresh = () => setData({ ...getLimits(), prime: getPrime() });
    window.addEventListener('brahmastra:usage', refresh);
    window.addEventListener('brahmastra:prime', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('brahmastra:usage', refresh);
      window.removeEventListener('brahmastra:prime', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const { tier, usage, remaining, prime } = data;
  const isPrime = tier.id === 'monthly';
  const devUnlimited = isDevUnlimited();

  return (
    <div className="mx-3 mb-2 p-3 rounded-xl bg-secondary/40 border border-border" data-testid="quota-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Crown className={`w-3.5 h-3.5 ${isPrime ? 'text-mint' : tier.id === 'test' ? 'text-bblue' : 'text-muted-foreground'}`} />
          <span className="text-[11px] font-semibold uppercase tracking-wider">
            {devUnlimited ? 'Dev · Unlimited' : tier.label}
          </span>
        </div>
        {!isPrime && !devUnlimited && (
          <button
            onClick={onUpgrade}
            data-testid="quota-upgrade-btn"
            className="text-[10px] text-mint hover:underline font-medium"
          >
            Upgrade
          </button>
        )}
      </div>

      {devUnlimited ? (
        <div className="text-[10.5px] text-muted-foreground">
          Test mode is active till deployment. Limits auto-enforce on production build.
        </div>
      ) : (
        <div className="space-y-1.5">
          <Row icon={MessageSquare} label="Chats" used={usage.chats} total={tier.chats} />
          <Row icon={Zap} label="Quizzes" used={usage.quizzes} total={tier.quizzes} />
          <Row icon={Layers} label="Flashcards" used={usage.flashcards} total={tier.flashcards} />
        </div>
      )}

      {prime.active && prime.expires_at && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          Expires {new Date(prime.expires_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, used, total }) => {
  const isInf = total === Infinity;
  const pct = isInf ? 0 : Math.min(100, Math.round((used / total) * 100));
  const low = !isInf && used >= total;
  return (
    <div>
      <div className="flex items-center justify-between text-[10.5px]">
        <span className="flex items-center gap-1 text-muted-foreground">
          <Icon className="w-2.5 h-2.5" /> {label}
        </span>
        <span className={`font-mono ${low ? 'text-destructive' : 'text-foreground/80'}`}>
          {used}/{isInf ? <Inf className="inline w-3 h-3 -mt-0.5" /> : total}
        </span>
      </div>
      <div className="mt-0.5 h-1 rounded-full bg-background overflow-hidden">
        <div
          className={`h-full transition-all ${low ? 'bg-destructive' : isInf ? 'bg-mint' : 'bg-mint'}`}
          style={{ width: isInf ? '100%' : `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default QuotaWidget;
